import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Loader2, Check, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = `/api/static/images/web/logo_mbs.png`;

export default function MagicLoginPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMsg("Lien invalide. Aucun token fourni.");
      return;
    }

    const authenticate = async () => {
      try {
        await api.post("/auth/magic-link", { token });
        await checkAuth();
        setStatus("success");
        setTimeout(() => navigate("/first-login", { replace: true }), 1500);
      } catch (err) {
        setStatus("error");
        setErrorMsg(err.response?.data?.detail || "Ce lien est invalide ou a expiré.");
      }
    };
    authenticate();
  }, [token, checkAuth, navigate]);

  return (
    <div className="min-h-screen bg-[#FBF9F5] text-[#333333] flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div aria-hidden="true" className="absolute inset-0 bg-tech-grid opacity-60" />
      <div aria-hidden="true" className="absolute -top-40 -left-32 w-[420px] h-[420px] rounded-full bg-[radial-gradient(circle,rgba(196,106,74,0.08),transparent_60%)]" />
      <div aria-hidden="true" className="absolute -bottom-40 -right-32 w-[420px] h-[420px] rounded-full bg-[radial-gradient(circle,rgba(0,140,168,0.08),transparent_60%)]" />
      <div className="relative w-full max-w-sm">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2.5">
            <img src={LOGO_URL} alt="MonBeauSite" className="h-12 w-12 object-contain rounded-xl ring-1 ring-[#D4AF37]/25 shadow-sm bg-white" />
            <span className="text-[17px] font-semibold text-[#1F3535] tracking-tight font-body">
              MonBeauSite<span className="text-[#C46A4A]">.</span>com
            </span>
          </Link>
        </div>

        <div className="bg-white/90 backdrop-blur-sm border border-[#BFBFBF]/45 rounded-2xl px-7 py-10 text-center shadow-sm">
          {status === "loading" && (
            <>
              <div className="relative w-14 h-14 mx-auto mb-5">
                <div className="absolute inset-0 rounded-full border-2 border-[#D4AF37]/25 border-t-[#C46A4A] animate-spin" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-[#1F3535]" />
                </div>
              </div>
              <p className="text-base font-medium text-[#1F3535] font-montserrat tracking-tight">
                Connexion <span className="font-playfair italic text-[#C46A4A] font-normal">en cours…</span>
              </p>
              <p className="text-[12px] text-[#6B6B6B] mt-2 font-body font-light">
                Veuillez patienter quelques instants.
              </p>
            </>
          )}
          {status === "success" && (
            <>
              <div className="w-14 h-14 rounded-full bg-[#1F3535]/[0.06] border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-5">
                <Sparkles className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <p className="text-lg font-medium text-[#1F3535] font-montserrat tracking-tight">
                <span className="font-playfair italic text-[#C46A4A] font-normal">Bienvenue</span> !
              </p>
              <p className="text-[12px] text-[#6B6B6B] mt-2 font-body font-light">
                Redirection vers votre espace…
              </p>
              <div className="mt-5 flex items-center justify-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]/70 animate-pulse" />
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]/70 animate-pulse" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]/70 animate-pulse" style={{ animationDelay: "300ms" }} />
              </div>
            </>
          )}
          {status === "error" && (
            <>
              <div className="w-14 h-14 rounded-full bg-red-50 border border-red-200 flex items-center justify-center mx-auto mb-5">
                <X className="w-6 h-6 text-red-500" strokeWidth={2.2} />
              </div>
              <p className="text-base font-medium text-[#1F3535] font-montserrat tracking-tight">
                Lien <span className="font-playfair italic text-[#C46A4A] font-normal">invalide</span>
              </p>
              <p className="text-[12px] text-[#6B6B6B] mt-3 mb-6 leading-relaxed font-body font-light">{errorMsg}</p>
              <Button
                onClick={() => navigate("/login")}
                className="bg-[#1F3535] text-white hover:bg-[#0F2424] rounded-full px-6 h-10 text-[11px] tracking-[0.16em] uppercase font-semibold shadow-md shadow-[#1F3535]/25"
              >
                Aller à la page de connexion
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
